import React, { useState, useEffect, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { Modal, Button, Table, Form, Alert ,Spinner} from 'react-bootstrap';
import moment from 'moment';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import Swal from 'sweetalert2';
import ViewVisitorRequest from './ViewVisitorRequest';

// DTOs
interface VisitorDTO {
  id: number;
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VehicleDTO {
  id: number;
  vehicleNumber: string;
  parkingSlotId: number | null;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VisitorRequestDTO {
  id: number;
  locationId: number;
  departmentId: number;
  responsiblePersonEmpNo: string;
  visitorPosition: string;
  visitorReason: string;
  parkingRequired: boolean;
  dateTime: string;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalComment: string | null;
  finalApprovedBy: string | null;
  finalApprovedAt: string | null;
  finalApprovalComment: string | null;
  status: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}

interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface BuildingDTO {
  id: number;
  name: string;
  noOfFloors: number;
  locationName: string;
  locationId: number;
  description?: string | null;
}

interface FloorDTO {
  id: number;
  name: string;
  floorNumber: number;
  buildingId: number;
  buildingName: string;
  locationName: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface ParkingSlotDTO {
  id: number;
  slotName: string;
  x: string;
  y: string;
  locationId?: number | null;
  floorId?: number | null;
  locationName?: string | null;
  floorName?: string | null;
  status: string;
}

interface GetVisitorsResponse {
  status: string;
  statusDescription: string;
  data: VisitorRequestDTO[];
  total: number;
}

interface GetParkingSlotsResponse {
  status: string;
  statusDescription: string;
  data: ParkingSlotDTO[];
}

interface GetLocationsResponse {
  status: string;
  statusDescription: string;
  data: { locations: LocationDTO[] };
}

interface FinalApprovalPayload {
  visitorRequestId: number;
  finalApprovalStatus: 'FINAL_APPROVED' | 'REJECTED';
  finalApprovalComment: string;
  parkingSlots: number[]; // payload to API stays numbers only
}

// form state: allow nulls while selecting
type FinalApprovalFormState =
  Omit<FinalApprovalPayload, 'parkingSlots'> & { parkingSlots: (number | null)[] };

interface ApprovedModuleProps {
  show: boolean;
  onHide: () => void;
}

const statusColors: { [key: string]: string } = {
  APPROVED: 'badge bg-success',
};

const ApprovedModule: React.FC<ApprovedModuleProps> = ({ show, onHide }) => {
  const [visitorRequests, setVisitorRequests] = useState<VisitorRequestDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFilter, setDateFilter] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVisitorRequestId, setSelectedVisitorRequestId] = useState<number | null>(null);
  const [showFinalApprovalModal, setShowFinalApprovalModal] = useState(false);
  const [finalApprovalForm, setFinalApprovalForm] = useState<FinalApprovalFormState>({
  visitorRequestId: 0,
  finalApprovalStatus: 'FINAL_APPROVED',
  finalApprovalComment: '',
  parkingSlots: [],
});

  const [finalApprovalError, setFinalApprovalError] = useState<string | null>(null);
  const [parkingSlots, setParkingSlots] = useState<ParkingSlotDTO[]>([]);
  const [selectedVisitorRequest, setSelectedVisitorRequest] = useState<VisitorRequestDTO | null>(null);
  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | ''>('');
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | ''>('');
  const [selectedFloorId, setSelectedFloorId] = useState<number | ''>('');

  const fetchVisitorRequests = useCallback(
  async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const payload = { filterType: 'STATUS', filterValue: 'APPROVED', date: dateFilter };
      const res: AxiosResponse<GetVisitorsResponse> = await apiDefinitions.getVisitors(page.toString(), payload);

      if (res.status === 200) {
        const data: GetVisitorsResponse = res.data;

        // Frontend filter: keep ONLY APPROVED items (case-insensitive just in case)
        const approvedOnly = (data.data || []).filter(
          (r) => (r.status || '').toUpperCase() === 'APPROVED'
        );

        setVisitorRequests(approvedOnly);
        // Note: server 'total' may include FINAL_APPROVED; use page-local approved count for UI paging
        setTotal(approvedOnly.length);
      } else {
        setError(res.data?.statusDescription || 'Failed to fetch approved visitor requests');
      }
    } catch (err: any) {
      setError('Failed to fetch approved visitor requests. Please try again.');
    } finally {
      setLoading(false);
    }
  },
  [dateFilter]
);


  const fetchLocations = useCallback(async () => {
    try {
      const payload = { filterType: '', filterValue: '', date: '' };
      const res: AxiosResponse<GetLocationsResponse> = await apiDefinitions.getLocations('1', payload);
      if (res.status === 200) {
        const data = res.data.data?.locations || [];
        setLocations(data);
      } else {
        setFinalApprovalError(res.data?.statusDescription || 'Failed to fetch locations');
      }
    } catch (err: any) {
      setFinalApprovalError('Failed to fetch locations. Please try again.');
    }
  }, []);

  const fetchBuildings = useCallback(async (locationId: number) => {
    try {
      const res: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(locationId);
      if (res.status === 200) {
        const data = res.data?.data || [];
        setBuildings(data);
      } else {
        setBuildings([]);
        setFinalApprovalError(res.data?.statusDescription || 'Failed to fetch buildings');
      }
    } catch (err: any) {
      setBuildings([]);
      setFinalApprovalError('Failed to fetch buildings. Please try again.');
    }
  }, []);

  const fetchFloors = useCallback(async (buildingId: number) => {
    try {
      const res: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(buildingId);
      if (res.status === 200) {
        const data = res.data?.data || [];
        setFloors(data);
      } else {
        setFloors([]);
        setFinalApprovalError(res.data?.statusDescription || 'Failed to fetch floors');
      }
    } catch (err: any) {
      setFloors([]);
      setFinalApprovalError('Failed to fetch floors. Please try again.');
    }
  }, []);

  const fetchParkingSlots = useCallback(async () => {
    if (!checkPermission('ROLE_GET_PARKING_SLOT')) {
      setFinalApprovalError('You do not have permission to view parking slots.');
      return;
    }
    try {
      const payload = {
        filterType: selectedFloorId ? 'floorId' : selectedBuildingId ? 'buildingId' : selectedLocationId ? 'locationId' : 'status',
        filterValue: selectedFloorId
          ? String(selectedFloorId)
          : selectedBuildingId
          ? String(selectedBuildingId)
          : selectedLocationId
          ? String(selectedLocationId)
          : 'available',
        date: '',
      };
      const res: AxiosResponse<GetParkingSlotsResponse> = await apiDefinitions.getAllParkingSlots('1', payload);
      if (res.status === 200) {
        const data = res.data.data || [];
        setParkingSlots(data);
        if (data.length === 0) {
          setFinalApprovalError('No available parking slots found for the selected filters.');
        }
      } else {
        setFinalApprovalError(res.data?.statusDescription || 'Failed to fetch parking slots');
      }
    } catch (err: any) {
      setFinalApprovalError('Failed to fetch parking slots. Please try again.');
    }
  }, [selectedLocationId, selectedBuildingId, selectedFloorId]);

  useEffect(() => {
    if (show) {
      setPageNo(1); // Reset to page 1 when modal opens
      fetchVisitorRequests(1);
    }
  }, [show, dateFilter, fetchVisitorRequests]);

  useEffect(() => {
    if (showFinalApprovalModal) {
      fetchLocations();
      fetchParkingSlots();
    }
  }, [showFinalApprovalModal, fetchLocations, fetchParkingSlots]);

  useEffect(() => {
    if (selectedLocationId) {
      fetchBuildings(Number(selectedLocationId));
      setSelectedBuildingId('');
      setSelectedFloorId('');
    } else {
      setBuildings([]);
      setFloors([]);
    }
  }, [selectedLocationId, fetchBuildings]);

  useEffect(() => {
    if (selectedBuildingId) {
      fetchFloors(Number(selectedBuildingId));
      setSelectedFloorId('');
    } else {
      setFloors([]);
    }
  }, [selectedBuildingId, fetchFloors]);

  useEffect(() => {
    if (showFinalApprovalModal) {
      fetchParkingSlots();
    }
  }, [selectedLocationId, selectedBuildingId, selectedFloorId, showFinalApprovalModal, fetchParkingSlots]);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= Math.ceil(total / 15)) {
      setPageNo(newPage);
      fetchVisitorRequests(newPage);
    }
  };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(e.target.value);
    setPageNo(1); // Reset to page 1 on date change
  };

  const handleViewClick = (visitorRequestId: number) => {
    setSelectedVisitorRequestId(visitorRequestId);
    setShowViewModal(true);
  };

  const handleFinalApproveClick = (visitorRequest: VisitorRequestDTO) => {
    if (!checkPermission('ROLE_FINAL_APPROVE_VISITOR_REQUEST')) {
      Swal.fire({
        icon: 'error',
        title: 'Permission Denied',
        text: 'You do not have permission to final approve visitor requests.',
      });
      return;
    }
    setSelectedVisitorRequest(visitorRequest);
    setFinalApprovalForm({
  visitorRequestId: visitorRequest.id,
  finalApprovalStatus: 'FINAL_APPROVED',
  finalApprovalComment: '',
  parkingSlots: Array(visitorRequest.vehicles.length).fill(null),
});

    setFinalApprovalError(null);
    setSelectedLocationId('');
    setSelectedBuildingId('');
    setSelectedFloorId('');
    setShowFinalApprovalModal(true);
  };

  const handleParkingSlotChange = (index: number, value: string) => {
  setFinalApprovalForm(prev => {
    const newParkingSlots = [...prev.parkingSlots];
    newParkingSlots[index] = value ? Number(value) : null;
    return { ...prev, parkingSlots: newParkingSlots };
  });
};


  const validateFinalApprovalForm = () => {
  if (!finalApprovalForm.finalApprovalComment.trim()) {
    setFinalApprovalError('Please provide a final approval comment.');
    return false;
  }
  if (selectedVisitorRequest?.parkingRequired && selectedVisitorRequest.vehicles.length > 0) {
    if (finalApprovalForm.parkingSlots.some(slot => slot === null)) {
      setFinalApprovalError('Please select a parking slot for each vehicle.');
      return false;
    }
    const asNumbers = finalApprovalForm.parkingSlots as number[];
    const uniqueSlots = new Set(asNumbers);
    if (uniqueSlots.size !== asNumbers.length) {
      setFinalApprovalError('Please select unique parking slots for each vehicle.');
      return false;
    }
    if (asNumbers.length !== selectedVisitorRequest.vehicles.length) {
      setFinalApprovalError('The number of selected parking slots must match the number of vehicles.');
      return false;
    }
  }
  return true;
};


  const handleFinalApprovalSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!checkPermission('ROLE_FINAL_APPROVE_VISITOR_REQUEST')) {
    setFinalApprovalError('You do not have permission to final approve visitor requests.');
    return;
  }
  if (!validateFinalApprovalForm()) return;

  try {
    setLoading(true);
    const payload: FinalApprovalPayload = {
      visitorRequestId: finalApprovalForm.visitorRequestId,
      finalApprovalStatus: finalApprovalForm.finalApprovalStatus,
      finalApprovalComment: finalApprovalForm.finalApprovalComment,
      parkingSlots: selectedVisitorRequest?.parkingRequired
        ? (finalApprovalForm.parkingSlots.filter((s): s is number => s !== null))
        : [],
    };
    const res: AxiosResponse = await apiDefinitions.finalApproveVisitor(payload);
      if (res.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Visitor request ${finalApprovalForm.finalApprovalStatus.toLowerCase()} successfully.`,
        });
        setShowFinalApprovalModal(false);
        fetchVisitorRequests(pageNo); // Refresh the list
      } else {
        setFinalApprovalError(res.data?.statusDescription || 'Failed to process final approval');
      }
    } catch (err: any) {
      setFinalApprovalError(err.response?.data?.statusDescription || 'Failed to process final approval. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Approved Visitor Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Filter by Date</label>
              <input
                type="date"
                className="form-control"
                value={dateFilter}
                onChange={handleDateFilterChange}
                disabled={loading}
              />
            </div>
          </div>

          {loading && (
            <div className="text-center">
              <Spinner animation="border" size="sm" /> Loading...
            </div>
          )}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && visitorRequests.length === 0 && (
            <div className="alert alert-info">No approved visitor requests found.</div>
          )}

          {!loading && !error && visitorRequests.length > 0 && (
            <div className="table-responsive">
              <Table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>Visitor Name</th>
                    <th>Company</th>
                    <th>Arrival Time</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitorRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.id}</td>
                      <td>{request.visitors[0]?.name || 'N/A'}</td>
                      <td>{request.visitors[0]?.visitorCompany || 'N/A'}</td>
                      <td>
                        {request.visitors[0]?.arrivalTime
                          ? moment(request.visitors[0].arrivalTime).format('YYYY-MM-DD HH:mm')
                          : 'N/A'}
                      </td>
                      <td>
                        <span className={statusColors[request.status] || 'badge bg-secondary'}>
                          {request.status || 'N/A'}
                        </span>
                      </td>
                      <td>{moment(request.dateTime).format('YYYY-MM-DD HH:mm')}</td>
                      <td>
                        <div className="hstack gap-2">
                          <Button
                            variant="icon btn-secondary btn-sm"
                            title="View Request"
                            onClick={() => handleViewClick(request.id)}
                          >
                            <i className="ti ti-eye" />
                          </Button>
                          <Button
                            variant="icon btn-success btn-sm"
                            title="Final Approve/Reject Request"
                            onClick={() => handleFinalApproveClick(request)}
                            disabled={!checkPermission('ROLE_FINAL_APPROVE_VISITOR_REQUEST')}
                          >
                            <i className="ti ti-check" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-primary"
              disabled={pageNo === 1 || loading}
              onClick={() => handlePageChange(pageNo - 1)}
            >
              Previous
            </Button>
            <span>
              Page {pageNo} of {Math.ceil(total / 15) || 1}
            </span>
            <Button
              variant="outline-secondary" 
              disabled={pageNo >= Math.ceil(total / 15) || loading}
              onClick={() => handlePageChange(pageNo + 1)}
            >
              Next
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={loading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Final Approval Modal */}
      <Modal show={showFinalApprovalModal} onHide={() => setShowFinalApprovalModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Final Approve/Reject Visitor Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFinalApprovalSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Final Approval Status</Form.Label>
              <Form.Select
                value={finalApprovalForm.finalApprovalStatus}
                onChange={(e) =>
                  setFinalApprovalForm({
                    ...finalApprovalForm,
                    finalApprovalStatus: e.target.value as 'FINAL_APPROVED' | 'REJECTED',
                  })
                }
                disabled={loading}
              >
                <option value="FINAL_APPROVED">Final Approved</option>
                <option value="REJECTED">Rejected</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Final Approval Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={finalApprovalForm.finalApprovalComment}
                onChange={(e) => setFinalApprovalForm({ ...finalApprovalForm, finalApprovalComment: e.target.value })}
                placeholder="Enter your comment (e.g., Approved)"
                disabled={loading}
                required
              />
            </Form.Group>
            {selectedVisitorRequest?.parkingRequired && selectedVisitorRequest.vehicles.length > 0 && (
              <div className="mb-3">
                <Form.Label>Parking Slot Filters</Form.Label>
                <div className="row g-2">
                  <Form.Group className="col-md-4">
                    <Form.Label>Location</Form.Label>
                    <Form.Select
                      value={selectedLocationId}
                      onChange={(e) => setSelectedLocationId(e.target.value ? Number(e.target.value) : '')}
                      disabled={loading}
                    >
                      <option value="">All Locations</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col-md-4">
                    <Form.Label>Building</Form.Label>
                    <Form.Select
                      value={selectedBuildingId}
                      onChange={(e) => setSelectedBuildingId(e.target.value ? Number(e.target.value) : '')}
                      disabled={loading || !selectedLocationId}
                    >
                      <option value="">All Buildings</option>
                      {buildings.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col-md-4">
                    <Form.Label>Floor</Form.Label>
                    <Form.Select
                      value={selectedFloorId}
                      onChange={(e) => setSelectedFloorId(e.target.value ? Number(e.target.value) : '')}
                      disabled={loading || !selectedBuildingId}
                    >
                      <option value="">All Floors</option>
                      {floors.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <Form.Label className="mt-3">Parking Slots</Form.Label>
                {selectedVisitorRequest.vehicles.map((vehicle, index) => (
                  <Form.Group key={index} className="mb-2">
                    <Form.Label>Vehicle {index + 1} ({vehicle.vehicleNumber})</Form.Label>
                    <Form.Select
                      value={finalApprovalForm.parkingSlots[index] || ''}
                      onChange={(e) => handleParkingSlotChange(index, e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Select a parking slot</option>
                      {parkingSlots.map((slot) => (
                        <option key={slot.id} value={slot.id}>
                          {slot.slotName} (Location: {slot.locationName || 'N/A'})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                ))}
              </div>
            )}
            {finalApprovalError && <Alert variant="danger">{finalApprovalError}</Alert>}
            <div className="d-flex justify-content-end">
              <Button
                variant="primary"
                onClick={() => setShowFinalApprovalModal(false)}
                className="me-2"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" variant="success" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* View Modal */}
      {selectedVisitorRequestId && (
        <ViewVisitorRequest
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          visitorRequestId={selectedVisitorRequestId}
        />
      )}
    </>
  );
};

export default ApprovedModule;